import React from "react";
//Limitless imports
import ClientContext from "@limitless/common/utils/client-context/ClientContext";
import { useTranslation } from "@limitless/common/hooks/translation/TranslationContext";
import { log as pilog } from "@limitless/common/utils";
import { validateCustomer } from "./CustomerValidator";
const UPDATE = "update";
const REPLACE = "replace";
export const INITIAL_CUSTOMER_STATE = {
  name: {
    value: "",
    pristine: true
  },
  customerLastName: {
    value: "",
    pristine: true
  },
  email: {
    value: "",
    pristine: true
  },
  question: {
    value: "",
    pristine: true
  },
  mobileNumber: {
    value: "",
    pristine: true
  },
  tenantSubject: {
    value: "",
    pristine: true
  },
  customerEmailHash: "",
  fieldErrors: {},
  secondaryFieldErrors: {},
  isFormInvalid: true,
  hiwViewed: false,
  emailViewed: false,
  shouldAutoSubmit: false,
  autoSubmittedQuestions: [],
  postHiwAction: "",
  customerKey: "",
  verificationCode: "",
  enforce: false
};
const getInitialCustomerState = (t, storageConfig) => {
  const initialState = {
    ...INITIAL_CUSTOMER_STATE
  };
  if (storageConfig && ClientContext._persistenceEnabled) {
    const log = pilog.getLogger("CustomerHook getInitialCustomerState");
    const localCustomer = storageConfig.getFromLocalStorage();
    try {
      initialState.name.value = localCustomer.user.name;
      initialState.name.pristine = false;
      initialState.email.value = localCustomer.user.email;
      initialState.email.pristine = false;
      initialState.customerLastName.value = localCustomer.user.customerLastName;
      initialState.customerLastName.pristine = false;
      initialState.customerEmailHash = localCustomer.user.customerEmailHash || "";
      initialState.hiwViewed = localCustomer.application.hiwViewed;
    } catch {
      log.info("Failed to rehydrate customer");
    }
  }
  if (ClientContext._userName) {
    initialState.name.value = ClientContext._userName;
    initialState.name.pristine = false;
  }
  if (ClientContext._userEmail) {
    initialState.email.value = ClientContext._userEmail;
    initialState.email.pristine = false;
  }
  if (ClientContext._userQuestion) {
    initialState.question.value = ClientContext._userQuestion;
    initialState.question.pristine = false;
  }
  if (ClientContext._userMobile) {
    initialState.mobileNumber.value = ClientContext._userMobile;
    initialState.mobileNumber.pristine = false;
  }
  if (ClientContext._userName && ClientContext._userEmail && ClientContext._userQuestion) {
    initialState.shouldAutoSubmit = true;
    if (ClientContext._userMobileEnabled === "true" && !ClientContext._userMobile) {
      initialState.shouldAutoSubmit = false;
    } else if (ClientContext._userCaptureSuppress === "name" && !ClientContext._userName) {
      initialState.shouldAutoSubmit = false;
    } else if (ClientContext._userCaptureSuppress === "email" && !ClientContext.userEmail) {
      initialState.shouldAutoSubmit = false;
    }
  }
  ClientContext.validateQueryParameters("emailHash");
  if (ClientContext._emailHash) {
    initialState.customerEmailHash = ClientContext._emailHash;
  }
  const {
    isFormInvalid,
    fieldErrors,
    secondaryFieldErrors
  } = validateCustomer(initialState, t);
  initialState.isFormInvalid = isFormInvalid;
  initialState.fieldErrors = fieldErrors;
  initialState.secondaryFieldErrors = secondaryFieldErrors;
  return initialState;
};
const customerReducer = (state, action) => {
  switch (action.type) {
    case REPLACE:
      return action.payload;
    case UPDATE:
      return {
        ...state,
        ...action.payload
      };
    default:
      throw new Error("Unsupported customer action type");
  }
};
export const useCustomer = storageConfig => {
  const {
    t
  } = useTranslation();
  const [customer, dispatch] = React.useReducer(customerReducer, getInitialCustomerState(t, storageConfig));
  React.useEffect(() => {
    if (!ClientContext._persistenceEnabled) {
      localStorage.removeItem(`persist:llm-${ClientContext._tenantName}`);
    }
  }, [ClientContext._persistenceEnabled, ClientContext._tenantName]);
  const updateCustomer = (key, value) => {
    const newCustomer = {
      ...customer,
      [key]: {
        value,
        pristine: false
      }
    };
    //validate the new changes
    const {
      isFormInvalid,
      fieldErrors,
      secondaryFieldErrors
    } = validateCustomer(newCustomer, t);
    newCustomer.isFormInvalid = isFormInvalid;
    newCustomer.fieldErrors = fieldErrors;
    newCustomer.secondaryFieldErrors = secondaryFieldErrors;
    if (key === "mobileNumber") {
      const mobileNumber = value;
      if (mobileNumber.length > 0) {
        if (mobileNumber.charAt(0) !== "+") {
          newCustomer.mobileNumber.value = `+${mobileNumber}`;
        }
      }
    }
    replaceAndSaveCustomer(newCustomer);
  };
  const replaceAndSaveCustomer = newCustomer => {
    dispatch({
      type: REPLACE,
      payload: newCustomer
    });
    if (storageConfig && ClientContext._persistenceEnabled) {
      storageConfig.saveToLocalStorage({
        user: {
          name: newCustomer.name.value,
          email: newCustomer.email.value,
          customerLastName: newCustomer.customerLastName.value,
          customerEmailHash: newCustomer.customerEmailHash
        },
        application: {
          hiwViewed: newCustomer.hiwViewed
        }
      });
    }
  };
  const updateAutoSubmit = shouldAutoSubmit => {
    dispatch({
      type: UPDATE,
      payload: {
        shouldAutoSubmit
      }
    });
  };
  const updateCustomerEmailHashAndClearQuestion = customerEmailHash => {
    clearQuestion();
    replaceAndSaveCustomer({
      ...customer,
      customerEmailHash
    });
  };
  const updateHIWViewedStatus = value => {
    dispatch({
      type: UPDATE,
      payload: {
        hiwViewed: value,
        postHiwAction: ""
      }
    });
  };
  const updateEmailViewed = value => {
    dispatch({
      type: UPDATE,
      payload: {
        emailViewed: value
      }
    });
  };
  const updateCustomerKey = value => {
    dispatch({
      type: UPDATE,
      payload: {
        customerKey: value
      }
    });
  };
  const addAutoSubmittedQuestion = newQuestion => {
    dispatch({
      type: UPDATE,
      payload: {
        autoSubmittedQuestions: [...customer.autoSubmittedQuestions, newQuestion]
      }
    });
  };
  const updatePostHiwAction = newAction => {
    dispatch({
      type: UPDATE,
      payload: {
        postHiwAction: newAction
      }
    });
  };
  const isReturningCustomer = () => {
    if (customer.name.pristine || customer.email.pristine) {
      return false;
    }
    //checks to see if all the customer details are present and correct (excluding question)
    const {
      isFormInvalid
    } = validateCustomer(customer, t, true);
    return !isFormInvalid;
  };
  const isCustomerQuestionValid = () => {
    const {
      isFormInvalid
    } = validateCustomer(customer, t, false);
    return !isFormInvalid;
  };
  const getCustomerData = () => {
    return {
      name: customer.name.value,
      email: customer.email.value,
      question: customer.question.value,
      mobileNumber: customer.mobileNumber.value,
      tenantSubject: customer.tenantSubject.value,
      customerLastName: customer.customerLastName.value,
      verificationCode: customer.verificationCode.value
    };
  };
  const clearQuestion = (extraState = {}) => {
    const newCustomer = {
      ...extraState
    };
    newCustomer.question = {
      value: "",
      pristine: true
    };
    newCustomer.tenantSubject = {
      value: "",
      pristine: true
    };
    newCustomer.isFormInvalid = true;
    dispatch({
      type: UPDATE,
      payload: newCustomer
    });
  };
  return {
    customer,
    updateCustomer,
    clearQuestion,
    getCustomerData,
    updateHIWViewedStatus,
    updateEmailViewed,
    updateCustomerEmailHashAndClearQuestion,
    isReturningCustomer,
    isCustomerQuestionValid,
    addAutoSubmittedQuestion,
    replaceAndSaveCustomer,
    updateAutoSubmit,
    updatePostHiwAction,
    updateCustomerKey
  };
};